<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountPlan" />
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }" v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-dropdown right no-caret variant="link" boundary="window" toggle-class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom title="Agregar cuentas">
                <template #button-content><AppIcon name="add" label="Agregar cuentas" /></template>
                <b-dropdown-item @click="add()"><AppIcon name="add" /> Agregar cuenta</b-dropdown-item>
                <b-dropdown-item @click="openImportCSV()"><AppIcon name="file-earmark-spreadsheet" /> Gestor de Importaciones</b-dropdown-item>
                <b-dropdown-item v-if="accounts && !accounts.length" @click="loadStandardPlan()"><AppIcon name="account_tree" /> Cargar plan estándar</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex:1 1 240px;max-width:360px;">
              <label class="filter-label">Buscar por código o nombre</label>
              <b-form-input size="sm" v-model="search" placeholder="Ej: caja o 1.01" />
            </div>
            <b-form-checkbox v-model="onlyActive" switch size="sm" class="mb-1">Solo activas</b-form-checkbox>
          </div>
        </b-collapse>
      </b-card>

      <div class="account-plan-kpis">
        <button v-for="section in sections" :key="section.value" type="button" class="account-plan-kpi" :class="['account-plan-kpi--' + section.tone, { 'account-plan-kpi--active': typeFilter == section.value }]" @click="toggleTypeFilter(section.value)">
          <span class="account-plan-kpi__icon"><AppIcon :name="section.icon" /></span>
          <span class="account-plan-kpi__body">
            <small>{{ section.text }}</small>
            <strong>{{ section.count }}</strong>
            <span>{{ section.count == 1 ? 'cuenta' : 'cuentas' }}</span>
          </span>
        </button>
      </div>

      <b-card no-body>
        <div class="account-plan-toolbar">
          <span class="list-view-toolbar__count">{{ visibleCount }} de {{ totalCount }} cuentas</span>
          <span>
            <b-button variant="link" size="sm" class="list-view-link" @click="expandAll()">Expandir todo</b-button>
            <b-button variant="link" size="sm" class="list-view-link" @click="collapseAll()">Contraer todo</b-button>
          </span>
        </div>

        <EmptyState v-if="accounts && !accounts.length" icon="account_tree" title="Todavía no hay plan de cuentas" description="Agregá la primera cuenta, importá el plan desde un archivo CSV o cargá el plan estándar." />
        <div class="text-center mb-3" v-if="accounts && !accounts.length">
          <b-button variant="dark" size="sm" @click="loadStandardPlan()"><AppIcon name="account_tree" class="mr-1" />Cargar plan estándar</b-button>
        </div>

        <template v-else>
          <section v-for="section in visibleSections" :key="section.value" class="account-plan-section">
            <header class="account-plan-section__header" :class="'account-plan-section__header--' + section.tone" @click="toggleSection(section.value)">
              <AppIcon class="account-plan-section__toggle" :name="isSectionOpen(section) ? 'expand_more' : 'chevron_right'" />
              <AppIcon :name="section.icon" />
              <div class="account-plan-section__title">
                <span class="account-plan-section__code" v-if="section.group">{{ section.group.code }}</span>
                <b>{{ section.text }}</b>
                <small>{{ section.description }}</small>
              </div>
              <span class="account-plan-section__actions" v-if="section.group" @click.stop>
                <b-button variant="link" class="list-view-icon-btn" v-b-tooltip.hover title="Editar grupo" aria-label="Editar grupo" @click="edit(section.group)"><AppIcon name="edit" /></b-button>
                <b-button variant="link" class="list-view-icon-btn" v-b-tooltip.hover title="Borrar grupo" aria-label="Borrar grupo" @click="remove(section.group)"><AppIcon name="delete" /></b-button>
              </span>
              <b-button variant="link" size="sm" class="list-view-link" @click.stop="add(section.value)"><AppIcon name="add" class="mr-1" />Agregar</b-button>
            </header>
            <template v-if="isSectionOpen(section)">
              <AccountTreeNode v-for="node in section.rows" :key="node.id" :node="node" :parent-type="section.group ? section.value : ''" :expanded="expanded" :force-open="isFiltering" :uses="accountUses"
                               @toggle="toggle" @add-child="addChild" @edit="edit" @remove="remove" />
              <p class="account-plan-section__empty" v-if="!section.rows.length">{{ isFiltering ? 'Sin coincidencias.' : 'Sin cuentas de este tipo.' }}</p>
            </template>
          </section>
        </template>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
        </div>
      </b-card>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- CRUD IMPORTACIONES -->
      <b-modal  v-model="modal.formImport.active"
                size="lg"
                :key="modal.formImport._id"
                :id="modal.formImport._id">                
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="upload_file" /></span>
          <h5 class="app-modal-header__title">{{ modal.formImport.title }}</h5>
        </div>
        
        <b-row>
        <b-col md="12" class="mb-2">
          <b-alert show variant="info">
              <h4 class="alert-heading">Importación de Plan de Cuentas</h4>
              
              <div>
                <b-icon icon="arrow-right-short"/> El sistema importará todos los registros cargados en el archivo.
              </div>
              <div>
                <b-icon icon="arrow-right-short"/> Esta importación permite agregar <b>grupos de cuentas</b> con sus <b>cuentas</b>.
              </div>                      
              <div>
                <b-icon icon="arrow-right-short"/> Si el <b>código</b> ya existe en la base de datos, se omite dicho registro. 
              </div>
              
              <hr>

              <div class="mb-0">
                Descargue el <a href="javascript:void(0);" @click="goDownloadFormat('examples-imports/accounting/csv-format-accounts.csv')"><b>formato de ejemplo</b></a> para esta importación.
              </div>                                                                  
            </b-alert>                  
        </b-col>
        <b-col md="12" class="mb-2">                  
          <b-form-group label="Archivo CSV [Plan de Cuentas]">                
            <b-form-file v-model="crud.formImport.file"
                          size="sm"
                          :state="Boolean(crud.formImport.file)"
                          placeholder="Elija un archivo o sueltelo aquí..."
                          drop-placeholder="Suelta el archivo aquí...">
            </b-form-file>                    
          </b-form-group>         
        </b-col> 
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.formImport.active=false"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" @click="importCSV()"><AppIcon name="upload_file" class="mr-1" />Importar</b-button>          
        </div>

      </b-modal>

      <!-- CRUD - EDIT -->
      <b-modal v-model="modal.form.active" size="lg" :key="modal.form._id" :id="modal.form._id">
        <template #modal-header>
          <div class="app-modal-header">
            <span class="app-modal-header__icon"><AppIcon name="account_tree" /></span>
            <h5 class="app-modal-header__title">{{ modal.form.title }}</h5>
          </div>
        </template>

        <label class="filter-label">Tipo de cuenta</label>
        <div class="account-plan-types mb-3">
          <button v-for="option in formTypes" :key="option.value" type="button" class="account-plan-type" :class="{ 'account-plan-type--active': crud.form.type == option.value }" @click="selectType(option.value)">
            <AppIcon :name="option.icon" />
            <b>{{ option.text }}</b>
            <small>{{ option.description }}</small>
          </button>
        </div>

        <div :key="crud.form.parent_id + '_' + crud.form.type">
          <FindObject render="search"
                      tag="Va dentro de (opcional)"
                      type="accountingAccounts"
                      @select-object="loadAccountingAccounts($event)"
                      :where="conditionAccountingAccounts"
                      :valueID="crud.form.parent_id"/>
        </div>

        <b-row>
          <b-col md="4">
            <b-form-group label="Código" description="Se sugiere solo; podés cambiarlo.">
              <b-form-input type="text" size="sm" v-model="crud.form.code"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="8">
            <b-form-group label="Nombre">
              <b-form-input type="text" size="sm" v-model="crud.form.name" placeholder="Ej: CAJA"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-checkbox v-model="crud.form.active" switch size="sm" class="mb-2">Cuenta activa</b-form-checkbox>

        <b-button variant="link" size="sm" class="list-view-link p-0" @click="modal.form.more = !modal.form.more">
          <AppIcon :name="modal.form.more ? 'expand_more' : 'chevron_right'" /> Más opciones
        </b-button>
        <b-collapse v-model="modal.form.more">
          <b-row class="mt-2">
            <b-col md="6">
              <b-form-group label="Referencia">
                <b-form-input type="text" size="sm" v-model="crud.form.reference"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Abreviación">
                <b-form-input type="text" size="sm" v-model="crud.form.abbreviation"></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-collapse>

        <template #modal-footer>
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false" size="sm"><AppIcon name="close" class="mr-1" />Cancelar</b-button>
          <b-button variant="dark" @click="save()" size="sm"><AppIcon name="save" class="mr-1" />Guardar</b-button>
        </template>
      </b-modal>

    </b-col>
  </b-row>
</template>
<script>
  import AccountingHelp from './inc/AccountingHelp'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper' 
  import StorageLocal from '@/handler/storageLocal'
  import FindObject from '@/components/inc/find/findObject'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import AccountTreeNode from './inc/AccountTreeNode'

  const ACCOUNT_TYPES = [
    { value: 'Activo', text: 'Activo', icon: 'account_balance_wallet', tone: 'info', description: 'Lo que la empresa tiene o le deben.' },
    { value: 'Pasivo', text: 'Pasivo', icon: 'credit_card', tone: 'warning', description: 'Lo que la empresa debe.' },
    { value: 'Patrimonio', text: 'Patrimonio neto', icon: 'savings', tone: 'primary', description: 'Capital y resultados. Acá va Resultado del Ejercicio.' },
    { value: 'Ingresos', text: 'Ingresos', icon: 'trending_up', tone: 'success', description: 'Ventas y otros ingresos. Se saldan en el cierre.' },
    { value: 'Egresos', text: 'Egresos', icon: 'trending_down', tone: 'danger', description: 'Compras y gastos. Se saldan en el cierre.' },
  ]
  const LEGACY_TYPE = { value: 'Resultado', text: 'Resultado', icon: 'help', tone: 'neutral', description: 'El cierre no toma este tipo. Conviene pasarlas a Patrimonio neto.' }
  // Texto del badge según el rol de la cuenta en cada ajuste: [principal, secundaria]
  const SETTING_LABELS = {
    customers: ['Clientes · a cobrar', 'Clientes · ingreso'],
    suppliers: ['Proveedores · a pagar', 'Proveedores · gasto'],
    products: ['Productos'],
    iva: ['IVA · ventas', 'IVA · compras'],
    perceptions: [null, 'Percepciones'],
    services: ['Servicios'],
    cash: ['Cajas'],
    valores: ['Valores'],
    resultado_ejercicio: ['Resultado del Ejercicio'],
  }

  export default {
    components: {
      AccountingHelp,
      AccountTreeNode,
      TabHeaderActions,
      FindObject
    },    
    data: () => {      
      return {      
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountPlan',
          elements: {}
        },
        crud: {
          form: {
            id: 0,
            code: '',
            reference: '',
            name: '',
            abbreviation: '',
            type: '',
            parent_id: 0,
            parent: null,
            active: true,
          },
          formImport: {
            file: '',
            type: ''            
          }            
        }, 
        modal: {
          form: {
            active: false,
            title: '',
            more: false,
          },
          formImport: {
            active: false,
            title: ''
          }          
        },   
        accounts: null,
        settings: [],
        expanded: {},
        collapsedSections: {},
        filtersOpen: false,
        search: '',
        onlyActive: false,
        typeFilter: null,
      }
    },
    created () {     
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)    
      /* Fin configuracion */
    },  
    mounted() {
      this._start = true
      this.filtersOpen = StorageLocal.getValue('filters_open_account_plan') == 'true'
      this.loadPlanAccount()
      this.loadSettings()
    },
    // Con keep-alive, al volver: las cuentas por defecto pudieron cambiar en Configuración
    activated() {
      if(this._start) {
        this._start = false
        return false
      }

      this.loadSettings()
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_account_plan', value ? 'true' : 'false')
      },
    },
    computed :{
      isFiltering() {
        return !!this.search.trim() || this.onlyActive
      },
      sectionTypes() {
        const hasLegacy = (this.accounts || []).some(node => node.type == LEGACY_TYPE.value)
        return hasLegacy ? ACCOUNT_TYPES.concat(LEGACY_TYPE) : ACCOUNT_TYPES
      },
      formTypes() {
        return this.crud.form.type == LEGACY_TYPE.value ? ACCOUNT_TYPES.concat(LEGACY_TYPE) : ACCOUNT_TYPES
      },
      sections() {
        const roots = this.accounts || []
        return this.sectionTypes.map(type => {
          const own = roots.filter(node => node.type == type.value)
          const nodes = own.map(node => this.filterNode(node)).filter(Boolean)
          const merged = this.isSectionGroup(own, type)
          const group = merged ? (nodes[0] || Object.assign({}, own[0], { children: [] })) : null
          return Object.assign({}, type, {
            count: own.reduce((total, node) => total + this.countNodes(node), 0),
            nodes,
            group,
            rows: group ? group.children : nodes,
          })
        })
      },
      visibleSections() {
        return this.sections.filter(section => {
          if(this.typeFilter) return section.value == this.typeFilter
          return !this.isFiltering || section.nodes.length
        })
      },
      totalCount() {
        return this.sections.reduce((total, section) => total + section.count, 0)
      },
      visibleCount() {
        return this.visibleSections.reduce((total, section) => total + section.nodes.reduce((sum, node) => sum + this.countNodes(node), 0), 0)
      },
      conditionAccountingAccounts(){
        if(this.crud.form.type) {
          return [
            {field: 'active', condition: true},
            {field: 'type', condition: this.crud.form.type}
          ];
        } else {
          return [
            {field: 'active', condition: true},
          ]
        }
      },         
    },
    methods: {                   
      loadPlanAccount(){
        let loader = this.$loading.show()

        serviceAPI.filtrarPlanCuenta()
        .then((response) => {
          loader.hide()
          this.accounts = this.sortNodes(response.data.map(element => this.buildNode(element)))
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        });
      },
      loadSettings() {
        serviceAPI.obtenerCuentaContableSettings()
        .then((response) => {
          this.settings = response.data
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        });
      },
      buildNode(element) {
        return {
          id: element.id,
          code: element.code,
          reference: element.reference,
          name: element.name,
          abbreviation: element.abbreviation,
          type: element.type,
          parent_id: element.parent_id,
          active: element.active == 1,
          children: this.sortNodes((element.children_recurrent || []).map(child => this.buildNode(child))),
        }
      },
      sortNodes(nodes) {
        return nodes.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }))
      },
      countNodes(node) {
        return node.children.reduce((total, child) => total + this.countNodes(child), 1)
      },
      // El grupo raíz que se llama igual que su tipo se muestra en la franja, para no repetirlo
      isSectionGroup(own, type) {
        const name = own.length == 1 ? String(own[0].name).trim().toLowerCase() : ''
        return !!own.length && own.length == 1 && own[0].children.length > 0
               && (name == type.value.toLowerCase() || name == type.text.toLowerCase())
      },
      isSectionOpen(section) {
        return this.isFiltering || !this.collapsedSections[section.value]
      },
      toggleSection(type) {
        this.$set(this.collapsedSections, type, !this.collapsedSections[type])
      },
      collapseAll() {
        const collapsed = {}
        this.sectionTypes.forEach(type => {
          collapsed[type.value] = true
        })
        this.collapsedSections = collapsed
        this.expanded = {}
      },
      // Devuelve el nodo si coincide él o alguna subcuenta; si coincide él, conserva sus subcuentas
      filterNode(node, parentMatches = false) {
        const term = this.search.trim().toLowerCase()
        const termMatches = parentMatches || !term || String(node.code).toLowerCase().includes(term) || String(node.name).toLowerCase().includes(term)
        const children = node.children.map(child => this.filterNode(child, termMatches && !!term)).filter(Boolean)
        const matches = termMatches && (!this.onlyActive || node.active)
        if(!matches && !children.length) return null
        return Object.assign({}, node, { children })
      },
      accountUses(id) {
        const uses = []
        this.settings.forEach(item => {
          const labels = SETTING_LABELS[item.reference] || [item.reference]
          if(item.accounting_accounts_id == id && labels[0]) uses.push(labels[0])
          if(item.accounting_accounts_secondary_id == id) uses.push(labels[1] || labels[0])
        })
        return uses
      },
      toggleTypeFilter(type) {
        this.typeFilter = this.typeFilter == type ? null : type
      },
      toggle(id) {
        this.$set(this.expanded, id, !this.expanded[id])
      },
      expandAll() {
        const expanded = {}
        const walk = node => {
          expanded[node.id] = true
          node.children.forEach(walk)
        }
        const roots = this.accounts || []
        roots.forEach(walk)
        this.expanded = expanded
        this.collapsedSections = {}
      },
      findNode(id, nodes = this.accounts || []) {
        for(const node of nodes) {
          if(node.id == id) return node
          const found = this.findNode(id, node.children)
          if(found) return found
        }
        return null
      },
      // Siguiente código libre entre las cuentas del mismo nivel: 1.01, 1.02...
      suggestCode(parent) {
        const siblings = parent ? parent.children : (this.accounts || [])
        const last = list => Number(String(list.code).split('.').pop()) || 0
        const next = siblings.reduce((max, node) => Math.max(max, last(node)), 0) + 1
        return parent ? parent.code + '.' + String(next).padStart(2, '0') : String(next)
      },

      // Numeración habitual del plan: 1 Activo, 2 Pasivo, 3 Patrimonio, 4 Ingresos, 5 Egresos
      suggestRootCode(type) {
        const position = ACCOUNT_TYPES.findIndex(item => item.value == type) + 1
        const taken = (this.accounts || []).some(node => String(node.code) == String(position))
        return position && !taken ? String(position) : this.suggestCode(null)
      },

      openForm(title, form, more) {
        Object.assign(this.crud.form, form)
        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = title
        this.modal.form.more = more
        this.modal.form.active = true
      },
      add(type) {
        if(type) this.$set(this.collapsedSections, type, false)
        // Si el tipo tiene un único grupo, la cuenta nueva va adentro
        const roots = (this.accounts || []).filter(node => node.type == type)
        if(roots.length == 1 && roots[0].children.length) {
          this.addChild(roots[0])
          return
        }

        this.openForm('Agregar cuenta', {
          id: 0,
          code: this.suggestRootCode(type),
          reference: '',
          name: '',
          abbreviation: '',
          parent_id: 0,
          parent: null,
          active: true,
          type: type || '',
        }, false)
      },
      addChild(parent) {
        this.$set(this.expanded, parent.id, true)
        this.openForm('Agregar subcuenta de ' + parent.name, {
          id: 0,
          code: this.suggestCode(this.findNode(parent.id) || parent),
          reference: '',
          name: '',
          abbreviation: '',
          parent_id: parent.id,
          parent: parent,
          active: true,
          type: parent.type,
        }, false)
      },
      edit(node) {
        this.openForm(node.children.length ? 'Editar grupo de cuentas' : 'Editar cuenta', {
          id: node.id,
          code: node.code,
          reference: node.reference,
          name: node.name,
          abbreviation: node.abbreviation,
          parent_id: node.parent_id,
          parent: null,
          active: node.active,
          type: node.type,
        }, !!(node.reference || node.abbreviation))
      },
      selectType(type) {
        if(this.crud.form.type == type) return
        this.crud.form.type = type
        this.crud.form.parent = null
        this.crud.form.parent_id = 0
        if(!this.crud.form.id) {
          this.crud.form.code = this.suggestRootCode(type)
        }
      },
      // Una cuenta no puede quedar dentro de sí misma o de una subcuenta suya
      isInvalidParent() {
        if(!this.crud.form.id || !this.crud.form.parent_id) return false
        const own = this.findNode(this.crud.form.id)
        return !!own && (own.id == this.crud.form.parent_id || !!this.findNode(this.crud.form.parent_id, own.children))
      },
      save() {
        if(this.isInvalidParent()) {
          this.$awn.alert('Una cuenta no puede quedar dentro de sí misma ni de una de sus subcuentas')
          return
        }

        let loader = this.$loading.show();
        
        if (this.crud.form.id) {
          var result = serviceAPI.editarPlanCuenta(this.crud.form);
        } else {
          result = serviceAPI.agregarPlanCuenta(this.crud.form);
        }

        result.then(() => {
          this.modal.form.active = false
          loader.hide()
          this.loadPlanAccount()
          this.$awn.success("Datos guardados con éxito");
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error));
        })
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: options.icon || (danger ? 'warning' : 'help') } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      remove(params) {
        var isGroup = params.children.length > 0
        this.confirmDialog("¿Desea borrar " + (isGroup ? "el grupo" : "la cuenta") + " '" + params.name + "'?", {
          title: isGroup ? 'Borrar grupo de cuentas' : 'Borrar cuenta',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO',
        })
        .then(value => {
          if (value) {

            let loader = this.$loading.show();
            var result = serviceAPI.eliminarPlanCuenta(params.id);

            result.then(() => {              
              loader.hide()
              this.loadPlanAccount()
              this.$awn.success("Registro eliminado");
            })
            .catch(error => {              
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error));
        })        
      },

      loadAccountingAccounts(object) {
        if(object){
          this.crud.form.parent = object
          this.crud.form.parent_id = object.id
          if(!this.crud.form.id) {
            this.crud.form.code = this.suggestCode(this.findNode(object.id))
          }
        } else {
          this.crud.form.parent = null
          this.crud.form.parent_id = 0
        }
      }, 
            
      // Import By CSV
      openImportCSV() {
        this.crud.formImport.type = 'import_store_account_plan'
        this.crud.formImport.file = ''
            
        this.modal.formImport._id = Math.random().toString(36).slice(2, 8)
        this.modal.formImport.title = "Gestor de Importaciones"
        this.modal.formImport.active = true
      },
      loadStandardPlan() {
        this.confirmDialog('Se carga un plan de cuentas básico y se completan las cuentas generales de Configuración. Después podés modificarlo. ¿Continuar?', {
          title: 'Cargar plan estándar',
          icon: 'account_tree',
          okTitle: 'Cargar',
        })
        .then(value => {
          if(!value) return

          let loader = this.$loading.show()
          serviceAPI.cargarPlanEstandar()
          .then(response => {
            loader.hide()
            this.loadPlanAccount()
            this.$awn.success(response.data.total + ' cuentas cargadas')
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      importCSV() {        
        this.confirmDialog('¿Continuar con la importación?', {
          title: 'Importar desde CSV',
          icon: 'upload_file',
          okTitle: 'Importar',
        })
        .then(value => {          
          if (value) {                       
            let loader = this.$loading.show();

            var file = new FormData();
            file.append("file", this.crud.formImport.file);            
              
            var result = serviceAPI.importarPlanCuenta(JSON.stringify(this.crud.formImport), file);

            result.then(() => {              
              this.modal.formImport.active = false              
              loader.hide()
              this.loadPlanAccount()
              this.$awn.success("Archivo importado con éxito");
            })
            .catch(error => {    
              loader.hide()
              this.$awn.alert(Error.showError(error));
            })
          }
        })
        .catch(error => {         
          this.$awn.alert(Error.showError(error));
        })        
      },
      goDownloadFormat(value) {
        var win = window.open(Helper.getPathPublic(value), '_blank');        
        win.focus();
      }      
    }  
  }      
</script>
<style scoped>
  .account-plan-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
    margin-bottom: 16px;
  }
  .account-plan-kpi {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    text-align: left;
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    cursor: pointer;
  }
  .account-plan-kpi:hover {
    border-color: var(--app-color-border-strong);
  }
  .account-plan-kpi--active {
    border-color: var(--app-color-primary);
    box-shadow: inset 0 0 0 1px var(--app-color-primary);
  }
  .account-plan-kpi__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    border-radius: 8px;
    background: var(--tone-soft);
    color: var(--tone);
  }
  .account-plan-kpi__body small {
    display: block;
    color: var(--app-color-text-secondary);
  }
  .account-plan-kpi__body strong {
    font-size: 17px;
  }
  .account-plan-kpi__body span {
    margin-left: 4px;
    font-size: 11px;
    color: var(--app-color-text-muted);
  }
  .account-plan-kpi--info, .account-plan-section__header--info { --tone: var(--app-color-info); --tone-soft: var(--app-color-info-soft); }
  .account-plan-kpi--warning, .account-plan-section__header--warning { --tone: var(--app-color-warning); --tone-soft: var(--app-color-warning-soft); }
  .account-plan-kpi--primary, .account-plan-section__header--primary { --tone: var(--app-color-primary); --tone-soft: var(--app-color-primary-soft); }
  .account-plan-kpi--success, .account-plan-section__header--success { --tone: var(--app-color-success); --tone-soft: var(--app-color-success-soft); }
  .account-plan-kpi--danger, .account-plan-section__header--danger { --tone: var(--app-color-danger); --tone-soft: var(--app-color-danger-soft); }
  .account-plan-kpi--neutral, .account-plan-section__header--neutral { --tone: var(--app-color-text-secondary); --tone-soft: var(--app-color-surface-subtle); }
  .account-plan-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border-bottom: 1px solid var(--app-color-border);
  }
  .account-plan-section__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: var(--tone-soft);
    color: var(--tone);
  }
  .account-plan-section__header {
    cursor: pointer;
  }
  .account-plan-section__toggle {
    color: var(--app-color-text-secondary);
  }
  .account-plan-section__title {
    flex: 1;
    min-width: 0;
  }
  .account-plan-section__code {
    margin-right: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--app-color-text-secondary);
  }
  .account-plan-section__actions {
    display: flex;
    opacity: 0;
  }
  .account-plan-section__header:hover .account-plan-section__actions,
  .account-plan-section__actions:focus-within {
    opacity: 1;
  }
  @media (max-width: 767.98px) {
    .account-plan-section__actions {
      opacity: 1;
    }
  }
  .account-plan-section__title small {
    margin-left: 8px;
    color: var(--app-color-text-secondary);
  }
  .account-plan-section__empty {
    margin: 0;
    padding: 10px 40px;
    font-size: 13px;
    color: var(--app-color-text-muted);
  }
  .account-plan-types {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(135px, 1fr));
    gap: 8px;
  }
  .account-plan-type {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    text-align: left;
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    cursor: pointer;
  }
  .account-plan-type small {
    font-size: 11px;
    line-height: 1.3;
    color: var(--app-color-text-secondary);
  }
  .account-plan-type--active {
    border-color: var(--app-color-primary);
    background: var(--app-color-primary-soft);
  }
</style>
